import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchCity } from "../lib/api";
import { ArrowLeft, MapPin, Users, Sparkles, BadgeCheck, ExternalLink } from "lucide-react";
import NewsletterForm from "../components/NewsletterForm";
import { useT } from "../i18n/I18nProvider";
import { formatMoneyFr } from "../lib/format";
import { NumbeoAttribution, NumbeoCategoryGrid } from "../components/NumbeoSections";

// Display order for rent rows (matches Numbeo keys)
const RENT_ORDER = ["rent_1br_center", "rent_1br_outside", "rent_3br_center", "rent_3br_outside"];
const BUY_ORDER  = ["buy_price_m2_center", "buy_price_m2_outside"];
// Categories shown in CityDetail "Prix du quotidien" (loyers/achat have dedicated tables above)
const CITY_DETAIL_CATEGORIES = [
  "restaurants_sorties", "courses", "transports",
  "charges_utilities", "garde_enfants_education", "salaires_finance", "sport_loisirs",
];

function RangeCell({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-slate-400">—</span>;
  }
  return <span className="tabular-nums">{formatMoneyFr(value)}</span>;
}

function findByKey(arr, key) {
  return (arr || []).find((it) => it.key === key);
}

export default function CityDetail() {
  const { t, lp } = useT();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => { setData(null); setErr(false); fetchCity(slug).then(setData).catch(() => setErr(true)); }, [slug]);

  // City pages own their <title>, og:title and twitter:title because those
  // values depend on async-loaded data. SeoHead intentionally skips title-
  // bearing tags on the /villes/:slug route, so there is no clobbering.
  useEffect(() => {
    const cityName = data?.city?.name_fr;
    if (!cityName) return;
    const title = `Vivre à ${cityName} : coût de la vie et logement | HejPoland`;
    document.title = title;
    const upsertMeta = (attrName, attrValue, content) => {
      const sel = `meta[${attrName}="${attrValue}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    upsertMeta("property", "og:title", title);
    upsertMeta("name", "twitter:title", title);
  }, [data]);

  if (err) return (
    <div className="max-w-3xl mx-auto px-4 py-24 text-center">
      <h1 className="font-display text-3xl">{t("city.notFound")}</h1>
      <Link to={lp("/")} className="hej-link mt-4 inline-block">{t("city.back2")}</Link>
    </div>
  );
  if (!data) return <div className="max-w-3xl mx-auto px-4 py-24 text-center text-slate-500">{t("city.loading")}</div>;

  const { city, budget } = data;
  const totalBudget = budget ? (budget.rent + budget.food + budget.transport + budget.utilities + budget.leisure + budget.other) : 0;
  const ext = city.external_data || {};
  const gusPop = ext.gus_population;
  const gusPrice = ext.gus_price_per_m2; // intentionally kept in memory but not rendered (see step 7)
  const displayPopulation = gusPop?.value ?? city.population;
  const populationYear = gusPop?.year;

  // Numbeo
  const numbeo = city.numbeo;
  const indicators = numbeo?.indicators || {};
  const loyers = indicators.loyers || [];
  const achat = indicators.achat_immobilier || [];

  return (
    <div data-testid="city-detail">
      <div className="relative h-72 md:h-96 overflow-hidden">
        {city.image_url && <img src={city.image_url} alt={city.name_fr} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 hej-hero-overlay" />
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-end pb-8 text-white">
          <Link to={lp("/")} className="inline-flex items-center gap-1.5 text-sm mb-4 hover:underline" data-testid="back-link"><ArrowLeft className="w-4 h-4" /> {t("city.back")}</Link>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">{city.name_fr}</h1>
          <p className="text-slate-200 text-sm mt-2">{city.name_pl} · {city.region}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-14">
          <div className="lg:col-span-2">
            <p className="text-slate-600 leading-relaxed text-lg">{city.summary}</p>
            {city.highlights?.length > 0 && (
              <ul className="mt-6 space-y-2.5">
                {city.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <Sparkles className="w-4 h-4 text-[#0056D2] flex-shrink-0 mt-1" />
                    <span className="text-slate-700">{h}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="bg-slate-50 rounded-2xl p-6 space-y-4 self-start" data-testid="city-stats-card">
            <div className="flex items-start gap-2 text-sm text-slate-600">
              <Users className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <span data-testid="city-population">{displayPopulation?.toLocaleString("fr-FR")}</span> {t("city.inhabitants")}
                {gusPop && (
                  <a href={gusPop.portal_url} target="_blank" rel="noopener noreferrer" className="block mt-1 text-[10px] text-slate-500 hover:text-[#0056D2] inline-flex items-center gap-1" data-testid="gus-pop-source">
                    <BadgeCheck className="w-3 h-3 text-emerald-600" />
                    GUS BDL · S1 {populationYear}
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600"><MapPin className="w-4 h-4" /> {city.region}</div>
            {/* GUS price/m² badge intentionally hidden (kept in code for future use). */}
            {false && gusPrice && (
              <div className="pt-4 border-t border-slate-200" data-testid="gus-price-block">
                <p className="text-xs text-slate-500 uppercase tracking-wide">Prix moyen au m² <span className="text-[10px] text-slate-400">· GUS officiel</span></p>
                <p className="font-display text-2xl font-bold text-slate-900 mt-1 tabular-nums">
                  {gusPrice.value?.toLocaleString("fr-FR")} <span className="text-base text-slate-500">zł/m²</span>
                </p>
                <a href={gusPrice.portal_url} target="_blank" rel="noopener noreferrer" className="mt-1 text-[10px] text-slate-500 hover:text-[#0056D2] inline-flex items-center gap-1" data-testid="gus-price-source">
                  <BadgeCheck className="w-3 h-3 text-emerald-600" />
                  GUS BDL · {gusPrice.year} {gusPrice.quarter}
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              </div>
            )}
            {budget && (
              <div className="pt-4 border-t border-slate-200">
                <p className="text-xs text-slate-500 uppercase tracking-wide">{t("city.budgetEstimated")}</p>
                <p className="font-display text-3xl font-bold text-slate-900 mt-1">{totalBudget.toLocaleString("fr-FR")} <span className="text-lg text-slate-500">zł</span></p>
              </div>
            )}
          </div>
        </div>

        {/* ===== Loyers (Numbeo) ===== */}
        {loyers.length > 0 && (
          <section className="mb-14" data-testid="rents-section">
            <h2 className="font-display text-2xl font-semibold text-slate-900 mb-5">
              {t("city.rentsTitle")} {city.name_fr}
            </h2>
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full text-sm" data-testid="rents-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">{t("city.rentsType")}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t("city.colMin")}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t("city.colAvg")}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t("city.colMax")}</th>
                  </tr>
                </thead>
                <tbody>
                  {RENT_ORDER.map((rk) => {
                    const row = findByKey(loyers, rk);
                    if (!row) return null;
                    return (
                      <tr key={rk} className="border-t border-slate-100" data-testid={`rent-row-${rk}`}>
                        <td className="px-4 py-3 text-slate-700">{row.label_fr}</td>
                        <td className="px-4 py-3 text-right text-slate-500"><RangeCell value={row.min} /></td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatMoneyFr(row.avg)}</td>
                        <td className="px-4 py-3 text-right text-slate-500"><RangeCell value={row.max} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <NumbeoAttribution numbeo={numbeo} testId="rents-attribution" />
          </section>
        )}

        {/* ===== Prix d'achat au m² (Numbeo) ===== */}
        {achat.length > 0 && (
          <section className="mb-14" data-testid="buy-section">
            <h2 className="font-display text-2xl font-semibold text-slate-900 mb-5">
              {t("city.buyTitle")}
            </h2>
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="w-full text-sm" data-testid="buy-table">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold">{t("city.buyLocation")}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t("city.colMin")}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t("city.colAvg")}</th>
                    <th className="px-4 py-3 text-right font-semibold">{t("city.colMax")}</th>
                  </tr>
                </thead>
                <tbody>
                  {BUY_ORDER.map((bk) => {
                    const row = findByKey(achat, bk);
                    if (!row) return null;
                    const isCenter = bk === "buy_price_m2_center";
                    return (
                      <tr key={bk} className="border-t border-slate-100" data-testid={`buy-row-${bk}`}>
                        <td className="px-4 py-3 text-slate-700">{isCenter ? t("housing.zoneCenter") : t("housing.zoneOutside")}</td>
                        <td className="px-4 py-3 text-right text-slate-500"><RangeCell value={row.min} /></td>
                        <td className="px-4 py-3 text-right font-semibold tabular-nums">{formatMoneyFr(row.avg)}</td>
                        <td className="px-4 py-3 text-right text-slate-500"><RangeCell value={row.max} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <NumbeoAttribution numbeo={numbeo} testId="buy-attribution" />
          </section>
        )}

        {/* ===== Prix du quotidien (Numbeo categories) ===== */}
        {numbeo && Object.keys(indicators).length > 0 && (
          <section className="mb-14" data-testid="daily-prices-section">
            <h2 className="font-display text-2xl font-semibold text-slate-900 mb-1">{t("city.dailyPricesTitle")}</h2>
            <p className="text-sm text-slate-500 mb-6">{t("city.dailyPricesIntro")}</p>
            <NumbeoCategoryGrid indicators={indicators} categories={CITY_DETAIL_CATEGORIES} />
            <NumbeoAttribution numbeo={numbeo} testId="daily-attribution" />
          </section>
        )}

        <section className="bg-slate-50 rounded-2xl p-6 md:p-8">
          <h2 className="font-display text-xl font-semibold text-slate-900 mb-2">{t("city.newsletterCity")} {city.name_fr}</h2>
          <p className="text-sm text-slate-600 mb-5">{t("city.newsletterCitySub")}</p>
          <NewsletterForm source={`city_${slug}`} />
        </section>
      </div>
    </div>
  );
}
